import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  makeStyles,
  useTheme,
  Button
} from "@material-ui/core";
import {
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Assignment as TicketIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  CloudQueue as SocketIcon
} from "@material-ui/icons";
import { getPerformanceMetrics } from "../../services/performance.api";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTimezone } from "../../hooks/useTimezone";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.mode === 'dark' 
        ? '0 4px 16px rgba(0,0,0,0.5)' 
        : '0 4px 16px rgba(0,0,0,0.15)',
    },
  },
  cardHeader: {
    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: '12px 12px 0 0',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  cardContent: {
    padding: theme.spacing(2),
  },
  appMetricCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    background: theme.mode === 'dark' 
      ? 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)' 
      : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
    border: `2px solid ${theme.mode === 'dark' ? '#3b82f6' : '#2196f3'}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  appMetricValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: theme.mode === 'dark' ? '#ffffff' : '#1976d2',
    marginTop: theme.spacing(1),
  },
  appMetricLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.mode === 'dark' ? '#93c5fd' : '#1565c0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  appMetricSubtext: {
    fontSize: '12px',
    color: theme.mode === 'dark' ? '#cbd5e1' : '#546e7a',
    marginTop: theme.spacing(0.5),
  },
  statBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: 12,
    fontSize: '11px',
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  statBadgePositive: {
    backgroundColor: theme.mode === 'dark' ? '#065f46' : '#d1fae5',
    color: theme.mode === 'dark' ? '#6ee7b7' : '#059669',
  },
  statBadgeNeutral: {
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#e5e7eb',
    color: theme.mode === 'dark' ? '#d1d5db' : '#6b7280',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  errorContainer: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.error.main,
  },
}));

const WhatsAppMetrics = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = React.useContext(AuthContext);
  const { formatDateTime } = useTimezone();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Cargar métricas
  const loadMetrics = async () => {
    try {
      setError(null);
      const response = await getPerformanceMetrics();
      
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        setMetrics(data);
      }
    } catch (err) {
      console.error('Error cargando métricas:', err);
      setError('Error al cargar métricas del servidor');
      toast.error('Error al cargar métricas del servidor');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Cargar métricas inicialmente y cada 5 segundos
  useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, 5000); // Actualizar cada 5 segundos
    
    return () => clearInterval(interval);
  }, []);

  if (loading && !metrics) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: 16 }}>
          Cargando métricas de la aplicación...
        </Typography>
      </Box>
    );
  }

  if (error && !metrics) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6">{error}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={loadMetrics}
          style={{ marginTop: 16 }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  if (!metrics || !metrics.application) {
    return (
      <Box className={classes.container}>
        <Typography variant="h6">No hay métricas disponibles</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* ✅ MÉTRICAS DE APLICACIÓN */}
      <Card className={classes.card}>
        <Box className={classes.cardHeader}>
          <TrendingUpIcon />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Métricas de la Aplicación
            {user?.super && (
              <Typography variant="caption" style={{ marginLeft: 8, opacity: 0.9, fontSize: '0.75rem' }}>
                (Vista Global - Todas las Empresas)
              </Typography>
            )}
          </Typography>
          <Box marginLeft="auto" display="flex" gap={1}>
            <Button
              size="small"
              startIcon={<ExportIcon />}
              onClick={() => {
                const dataStr = JSON.stringify(metrics.application, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `metricas-whatsapp-${new Date().toISOString().split('T')[0]}.json`;
                link.click();
                URL.revokeObjectURL(url);
              }}
              variant="outlined"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)'
              }}
            >
              Exportar
            </Button>
          </Box>
        </Box>
        <CardContent className={classes.cardContent}>
          <Typography className={classes.sectionTitle}>
            <TicketIcon /> Tickets
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <Box className={classes.appMetricCard}>
                <Typography className={classes.appMetricLabel}>Total</Typography>
                <Typography className={classes.appMetricValue}>
                  {metrics.application.tickets.total.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' 
                  : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                borderColor: theme.mode === 'dark' ? '#10b981' : '#10b981'
              }}>
                <Typography className={classes.appMetricLabel}>Abiertos</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#059669' }}>
                  {metrics.application.tickets.open.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
                  : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderColor: theme.mode === 'dark' ? '#f59e0b' : '#f59e0b'
              }}>
                <Typography className={classes.appMetricLabel}>Pendientes</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#d97706' }}>
                  {metrics.application.tickets.pending.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' 
                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                borderColor: theme.mode === 'dark' ? '#6b7280' : '#9ca3af'
              }}>
                <Typography className={classes.appMetricLabel}>Cerrados</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#4b5563' }}>
                  {metrics.application.tickets.closed.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' 
                  : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                borderColor: theme.mode === 'dark' ? '#3b82f6' : '#3b82f6'
              }}>
                <Typography className={classes.appMetricLabel}>Hoy</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#2563eb' }}>
                  {metrics.application.tickets.today.toLocaleString()}
                </Typography>
                <Typography className={classes.appMetricSubtext}>
                  Última hora: {metrics.application.tickets.lastHour}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' 
                  : 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                borderColor: theme.mode === 'dark' ? '#ec4899' : '#ec4899'
              }}>
                <Typography className={classes.appMetricLabel}>Última Hora</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#db2777' }}>
                  {metrics.application.tickets.lastHour.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography className={classes.sectionTitle} style={{ marginTop: theme.spacing(3) }}>
            <MessageIcon /> Mensajes
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
                  : 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
                borderColor: theme.mode === 'dark' ? '#8b5cf6' : '#8b5cf6'
              }}>
                <Typography className={classes.appMetricLabel}>Total Mensajes</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#7c3aed' }}>
                  {metrics.application.messages.total.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' 
                  : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                borderColor: theme.mode === 'dark' ? '#3b82f6' : '#3b82f6'
              }}>
                <Typography className={classes.appMetricLabel}>Mensajes Hoy</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#2563eb' }}>
                  {metrics.application.messages.today.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                  : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                borderColor: theme.mode === 'dark' ? '#10b981' : '#10b981'
              }}>
                <Typography className={classes.appMetricLabel}>Última Hora</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#059669' }}>
                  {metrics.application.messages.lastHour.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
                  : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderColor: theme.mode === 'dark' ? '#f59e0b' : '#f59e0b'
              }}>
                <Typography className={classes.appMetricLabel}>Por Minuto</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#d97706' }}>
                  {metrics.application.messages.perMinute}
                </Typography>
                <Typography className={classes.appMetricSubtext}>
                  Promedio últimos 60 min
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography className={classes.sectionTitle} style={{ marginTop: theme.spacing(3) }}>
            <PeopleIcon /> Contactos y Usuarios
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' 
                  : 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                borderColor: theme.mode === 'dark' ? '#6366f1' : '#6366f1'
              }}>
                <Typography className={classes.appMetricLabel}>Total Contactos</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#4f46e5' }}>
                  {metrics.application.contacts.total.toLocaleString()}
                </Typography>
                <Typography className={classes.appMetricSubtext}>
                  Activos: {metrics.application.contacts.active.toLocaleString()}
                  <span className={`${classes.statBadge} ${classes.statBadgePositive}`}>
                    +{metrics.application.contacts.today} hoy
                  </span>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' 
                  : 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                borderColor: theme.mode === 'dark' ? '#0ea5e9' : '#0ea5e9'
              }}>
                <Typography className={classes.appMetricLabel}>Total Usuarios</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#0284c7' }}>
                  {metrics.application.users.total.toLocaleString()}
                </Typography>
                <Typography className={classes.appMetricSubtext}>
                  En línea: {metrics.application.users.online}
                  <span className={`${classes.statBadge} ${classes.statBadgePositive}`}>
                    {metrics.application.users.active} activos
                  </span>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)' 
                  : 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                borderColor: theme.mode === 'dark' ? '#25d366' : '#25d366'
              }}>
                <Typography className={classes.appMetricLabel}>WhatsApp</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#128c7e' }}>
                  {metrics.application.whatsapp.connected}/{metrics.application.whatsapp.total}
                </Typography>
                <Typography className={classes.appMetricSubtext}>
                  Conectados
                  {metrics.application.whatsapp.disconnected > 0 && (
                    <span className={`${classes.statBadge} ${classes.statBadgeNeutral}`}>
                      {metrics.application.whatsapp.disconnected} desconectados
                    </span>
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
                  : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderColor: theme.mode === 'dark' ? '#f59e0b' : '#f59e0b'
              }}>
                <Typography className={classes.appMetricLabel}>Total Empresas</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#d97706' }}>
                  {metrics.application.companies?.total?.toLocaleString() || 0}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography className={classes.sectionTitle} style={{ marginTop: theme.spacing(3) }}>
            <SocketIcon /> Socket.IO
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' 
                  : 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)',
                borderColor: theme.mode === 'dark' ? '#06b6d4' : '#06b6d4'
              }}>
                <Typography className={classes.appMetricLabel}>Eventos/Minuto</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#0891b2' }}>
                  {metrics.application.socket.eventsPerMinute}
                </Typography>
                <Typography className={classes.appMetricSubtext}>
                  Tiempo real
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
                  : 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
                borderColor: theme.mode === 'dark' ? '#8b5cf6' : '#8b5cf6'
              }}>
                <Typography className={classes.appMetricLabel}>Total Eventos</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#7c3aed' }}>
                  {metrics.application.socket.totalEvents.toLocaleString()}
                </Typography>
                <Typography className={classes.appMetricSubtext}>
                  Desde inicio
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box className={classes.appMetricCard} style={{ 
                background: theme.mode === 'dark' 
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
                  : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderColor: theme.mode === 'dark' ? '#f59e0b' : '#f59e0b'
              }}>
                <Typography className={classes.appMetricLabel}>Conexiones</Typography>
                <Typography className={classes.appMetricValue} style={{ color: theme.mode === 'dark' ? '#ffffff' : '#d97706' }}>
                  {metrics.application.socket.activeConnections || 'N/A'}
                </Typography>
                <Typography className={classes.appMetricSubtext}>
                  Activas
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ✅ ÚLTIMA ACTUALIZACIÓN */}
      <Box textAlign="center" marginTop={2} display="flex" justifyContent="center" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="caption" color="textSecondary">
          Última actualización: {formatDateTime(metrics.timestamp)}
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadMetrics}
          variant="outlined"
          style={{ minWidth: 'auto', padding: '4px 12px' }}
        >
          Actualizar
        </Button>
        <Typography variant="caption" color="textSecondary">
          Auto-actualización cada 5 segundos
        </Typography>
      </Box>
    </Box>
  );
};

export default WhatsAppMetrics;

